// The families a browser cannot draw on its own (Word, Excel, PowerPoint,
// archives, anything unrecognised). The card says why, keeps the original one
// click away, shows a server-side text rendition when the host supplied one,
// and a hex dump of the first bytes when the type itself is in question.

import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '../../core/Button/Button.js';
import { Notice } from '../chrome.js';
import { hexDump } from '../format.js';
import type { RendererProps } from '../types.js';

export function UnsupportedRenderer({ file, detected, source, labels, register, download, openExternally }: RendererProps): ReactNode {
  const [text, setText] = useState<{ state: 'idle' | 'loading' | 'ready' | 'failed'; body?: string }>({ state: 'idle' });
  const [hex, setHex] = useState(false);

  useEffect(() => {
    register({
      capabilities: {},
      details: [{ label: labels.detailRenderer, value: text.state === 'ready' ? labels.rendererConverted : labels.rendererNone }]
    });
  }, [register, labels, text.state]);

  const message = (() => {
    switch (detected.family) {
      case 'spreadsheet': return labels.unsupportedSpreadsheet;
      case 'presentation': return labels.unsupportedPresentation;
      case 'document': return labels.unsupportedDocument;
      case 'archive': return labels.unsupportedArchive;
      default: return labels.unsupportedUnknown;
    }
  })();

  const loadText = () => {
    if (!source.textFallback) return;
    setText({ state: 'loading' });
    source.textFallback()
      .then((body) => setText({ state: 'ready', body }))
      .catch(() => setText({ state: 'failed' }));
  };

  const showHex = detected.family === 'unknown' || detected.family === 'archive' || detected.mismatch;

  return (
    <Notice family={detected.family} title={labels.unsupportedTitle} message={message} labels={labels} download={download} openExternally={openExternally}>
      {source.textFallback && (
        <section className="rr-fv__section">
          <div className="rr-fv__section-head">
            <div>
              <div className="rr-fv__section-title">{labels.convertedPreview}</div>
              <div className="rr-fv__section-hint">{labels.convertedPreviewHint}</div>
            </div>
            {text.state !== 'ready' && (
              <Button size="xs" variant="default" loading={text.state === 'loading'} onClick={loadText}>{labels.loadConvertedPreview}</Button>
            )}
          </div>
          {text.state === 'failed' && <div className="rr-fv__banner rr-fv__banner--warning">{labels.convertedPreviewFailed}</div>}
          {text.state === 'ready' && <pre className="rr-fv__pre rr-fv__pre--converted">{text.body || labels.emptyText}</pre>}
        </section>
      )}
      {showHex && file.head.length > 0 && (
        <section className="rr-fv__section">
          <div className="rr-fv__section-head">
            <div>
              <div className="rr-fv__section-title">{labels.hexPreview}</div>
              <div className="rr-fv__section-hint">{labels.hexPreviewHint}</div>
            </div>
            {!hex && <Button size="xs" variant="default" onClick={() => setHex(true)}>{labels.showHexPreview}</Button>}
          </div>
          {hex && <pre className="rr-fv__pre rr-fv__pre--hex">{hexDump(file.head, 512)}</pre>}
        </section>
      )}
    </Notice>
  );
}
